import i18n from "@/i18n";
import type { ApiResponse } from "@/api/client";
import { coordinateInputError, coordinatesForSave } from "@/lib/hotelCoordinatesInput";
import { formatCoordinateInput, hotelCoordinates } from "@ckgo2/shared";
import { UNSAVED_TRIP_PARTS, type UnsavedTripPart } from "@ckgo2/shared/schemas";
import type { Trip, TripPremiumUpgrade } from "@ckgo2/shared/types";
import { generateId, normalizeMeals } from "@ckgo2/shared/utils";
import type { TripComponentRow, TripFormData } from "./types";
import type { TripDate } from "./TripDatesEditor";

/**
 * Převody editoru zájezdu a hotelu: detail z `GET /api/admin/trips/:id` → hodnoty formuláře
 * (`tripFormFromDetail`) a hodnoty formuláře → tělo `PUT`/`POST` (`tripSaveBody`). Čisté funkce
 * bez Reactu, ať je drží test (`tripFormTransform.test.ts`, vitest projekt `admin`).
 *
 * Pravidlo obou směrů: data, která editor neukazuje, se neztrácejí. Položky JSON polí se
 * ROZPROSTÍRAJÍ (`...room`), ne vyjmenovávají po polích — výčet tiše zahodí každý klíč,
 * který editor nezná (dříve `dayEnd` itineráře). Klíč, který tělo nenese, PUT nezmění
 * (`tripUpdateSchema` je `partial()`), proto tělo nenese sloupce mimo editor.
 */

/** Hodnoty nového zájezdu (formulář před prvním uložením). */
export const TRIP_FORM_DEFAULTS: TripFormData = {
  title: "",
  slug: "",
  destinationId: "",
  destinationTagIds: [],
  guideId: "",
  guideVideoUrl: "",
  durationDays: 7,
  durationHours: null,
  priceFrom: 0,
  priceIsFrom: false,
  priceOriginal: null,
  shortDescription: "",
  description: "",
  featuredImageId: null,
  difficultyLevel: "medium",
  tripType: "guided",
  tripKind: "tour",
  fulfillmentMode: "fixed_departures",
  countryCodesText: "VN",
  optionalActivities: [],
  tripStyle: "",
  groupSizeMin: 8,
  groupSizeMax: 16,
  leadTimeHours: 24,
  availableWeekdays: null,
  status: "draft",
  isFeatured: false,
  isLastMinute: false,
  isForWomen: false,
  groupSizeOpenEnded: false,
  hasCzechGuide: false,
  heroTitle: "",
  heading: "",
  metaTitle: "",
  metaDescription: "",
  itinerary: [],
  galleryImageIds: [],
  included: [],
  excluded: [],
  tags: [],
  dates: [],
  youtubeVideos: [],
  note: "",
  guideRecommendation: "",
  defaultMeetingPoint: "",
  defaultMeetingTime: "",
  upgradeIds: [],
  faqs: [],
  socialReels: [],
  mapImageId: null,
  relatedItems: [],
  relatedPageIds: [],
  highlights: [],
  schedule: [],
  components: [],
  hotelStars: null,
  hotelRooms: [],
  hotelMealPlans: [],
  hotelDepartures: [],
  hotelFeatures: [],
  hotelCoordinatesText: "",
};

/** Prázdný katalog hotelu jde zpět jako `NULL` (sloupec bez hotelu nemá nést `[]`). */
export function emptyToNull<T>(list: T[]): T[] | null {
  return list.length > 0 ? list : null;
}

function transformTripTextFields(t: Trip) {
  return {
    title: t.title || "",
    slug: t.slug || "",
    destinationId: t.destinationId || "",
    destinationTagIds: t.destinationTagIds || [],
    guideId: t.guideId || "",
    guideVideoUrl: t.guideVideoUrl || "",
    shortDescription: t.shortDescription || "",
    description: t.description || "",
    featuredImageId: t.featuredImageId ?? null,
    heroTitle: t.heroTitle || "",
    heading: t.heading || "",
    metaTitle: t.metaTitle || "",
    metaDescription: t.metaDescription || "",
    mapImageId: t.mapImageId ?? null,
    note: t.note || "",
    guideRecommendation: t.guideRecommendation || "",
    defaultMeetingPoint: t.defaultMeetingPoint || "",
    defaultMeetingTime: t.defaultMeetingTime || "",
  };
}

function transformTripNumericAndFlagFields(t: Trip) {
  // Use ?? for nullable numbers/booleans to preserve 0/false; || for strings where empty is falsy.
  // durationHours stays null for tours (schema rejects 0 via .min(1)).
  return {
    durationDays: t.durationDays ?? 7,
    durationHours: t.durationHours ?? null,
    priceFrom: t.priceFrom ?? 0,
    priceIsFrom: t.priceIsFrom ?? false,
    priceOriginal: t.priceOriginal ?? null,
    difficultyLevel: t.difficultyLevel || "medium",
    tripType: t.tripType || "guided",
    tripKind: t.tripKind || "tour",
    fulfillmentMode: t.fulfillmentMode || "fixed_departures",
    countryCodesText: (t.countryCodes || ["VN"]).join(", "),
    optionalActivities: t.optionalActivities || [],
    tripStyle: t.tripStyle || "",
    groupSizeMin: t.groupSizeMin ?? 8,
    groupSizeMax: t.groupSizeMax ?? 16,
    leadTimeHours: t.leadTimeHours ?? 24,
    status: t.status || "draft",
    isFeatured: t.isFeatured ?? false,
    isLastMinute: t.isLastMinute ?? false,
    isForWomen: t.isForWomen ?? false,
    groupSizeOpenEnded: t.groupSizeOpenEnded ?? false,
    hasCzechGuide: t.hasCzechGuide ?? false,
    // Prázdné pole = hotel bez polohy. O tom, zda poloha JE, rozhoduje jen `hotelCoordinates`.
    hotelCoordinatesText: formatCoordinateInput(hotelCoordinates(t)),
  };
}

function transformTripArrayFields(t: Trip) {
  return {
    // Rozprostření, ne výčet polí: výčet tiše ztrácel `dayEnd` (rozsah „1.–2. den"), takže
    // se délka položky při každém dalším uložení vrátila na jeden den, a stejně tak
    // legacy `image`. Nové pole v `itineraryDaySchema` by propadlo úplně stejně.
    itinerary: (t.itinerary || []).map((day) => ({
      ...day,
      description: day.description || "",
      imageId: day.imageId ?? null,
      meals: normalizeMeals(day.meals),
      accommodation: day.accommodation || "",
      highlights: day.highlights || [],
      guideTip: day.guideTip || "",
    })),
    galleryImageIds: t.galleryImageIds || [],
    included: (t.included || []).map((text) => ({ text })),
    excluded: (t.excluded || []).map((text) => ({ text })),
    tags: t.tags || [],
    dates: ((t as Trip & { dates?: TripDate[] }).dates || []).map((date: TripDate) => ({
      id: date.id,
      startDate:
        typeof date.startDate === "string"
          ? date.startDate
          : new Date(date.startDate).toISOString().split("T")[0],
      endDate:
        typeof date.endDate === "string"
          ? date.endDate
          : new Date(date.endDate).toISOString().split("T")[0],
      price: date.price,
      capacity: date.capacity,
      bookedCount: date.bookedCount || 0,
      lifecycle: date.lifecycle,
      // Poznámku editor ukazuje i ukládá (`syncTripDates` ji zapisuje); bez ní by pole
      // „Poznámky (interní)“ vypadalo prázdné a první zápis by uloženou poznámku přepsal.
      notes: date.notes ?? undefined,
      // Jen se zobrazuje a posílá zpět beze změny (štítek „Soukromý“, F1.12).
      isPrivate: date.isPrivate ?? false,
    })),
    youtubeVideos: t.youtubeVideos || [],
    faqs:
      Array.isArray(t.faqs) && t.faqs.length > 0 && "question" in t.faqs[0]
        ? [
            {
              topic: i18n.t("trips.content.faqPrefill.topicGeneral"),
              items: t.faqs as unknown as Array<{ question: string; answer: string }>,
            },
          ]
        : t.faqs || [],
    socialReels: t.socialReels || [],
    relatedItems: t.relatedItems || [],
    relatedPageIds: t.relatedPageIds || [],
    availableWeekdays: t.availableWeekdays ?? null,
    highlights: t.highlights || [],
    schedule: t.schedule || [],
    // Hotel overlay — null columns load as empty catalogues so the editors stay controlled.
    hotelStars: t.hotelStars ?? null,
    // Rozprostření jako u `itinerary`: klíč pokoje, který editor nezná, se neztratí.
    hotelRooms: (t.hotelRooms || []).map((room) => ({
      ...room,
      id: room.id,
      name: room.name || "",
      occupancy: room.occupancy || "",
      beds: room.beds || "",
      sizeSqm: room.sizeSqm ?? null,
      description: room.description || "",
      surcharge: room.surcharge ?? 0,
      imageId: room.imageId ?? null,
    })),
    // `note` se sice needituje, ale veze se s sebou — `transformOnSave` tohle pole posílá
    // beze změny, takže vynechat ho tady znamená smazat poznámku při prvním uložení.
    hotelMealPlans: (t.hotelMealPlans || []).map((m) => ({
      ...m,
      plan: m.plan,
      surcharge: m.surcharge ?? 0,
      note: m.note ?? null,
    })),
    hotelDepartures: t.hotelDepartures || [],
    hotelFeatures: t.hotelFeatures || [],
    components: ((t as Trip & { components?: TripComponentRow[] }).components || []).map((cmp) => ({
      id: cmp.id,
      componentId: cmp.componentId,
      quantity: cmp.quantity ?? 1,
      seedTarget: cmp.seedTarget ?? "order",
      sortOrder: cmp.sortOrder ?? 0,
      notes: cmp.notes ?? null,
      component: cmp.component ?? null,
    })),
  };
}

/** Detail zájezdu → hodnoty formuláře. `upgradeIds` = přiřazení z keše `["tripUpgrades", id]`. */
export function tripFormFromDetail(t: Trip, upgradeIds?: readonly string[]): TripFormData {
  return {
    ...transformTripTextFields(t),
    ...transformTripNumericAndFlagFields(t),
    ...transformTripArrayFields(t),
    // Detail přiřazení nevrací (jdou z `GET /:id/upgrades`), proto druhý argument.
    upgradeIds: upgradeIds ? [...upgradeIds] : [],
  };
}

/**
 * Hodnoty formuláře → tělo uložení.
 *
 * `upgradeIds`: klíč v těle spouští `syncPremiumUpgrades`, který u existujícího zájezdu smaže
 * VŠECHNA přiřazení a vloží nová. Existující zájezd ho proto nese jen s načtenými přiřazeními
 * (`upgradeAssignmentsLoaded`); bez nich klíč chybí a API přiřazení nemění, nikdy `[]`
 * „pro jistotu“. Nový zájezd (`isNew`) ho nese vždy: `afterCreate` klíč čte a nic
 * k přepsání neexistuje (vize F6, dřív se výběr při prvním uložení ztratil).
 */
export function tripSaveBody(
  data: TripFormData,
  opts: { isNew: boolean; upgradeAssignmentsLoaded: boolean },
): Record<string, unknown> {
  const { countryCodesText, hotelCoordinatesText, upgradeIds, ...rest } = data;
  return {
    ...rest,
    ...(opts.isNew || opts.upgradeAssignmentsLoaded ? { upgradeIds } : {}),
    // Poloha hotelu: text pole → dvojice sloupců (prázdné = obě `null`). U resortu je text
    // v tuhle chvíli už ověřený obalem odeslání (`submitTrip`). Mimo resort je záložka Hotel
    // skrytá a obal text nehlídá: platný se uloží jako dřív, neplatný se zahodí a sloupce
    // se nepošlou (uložená poloha zůstane beze změny).
    ...(rest.tripType === "resort" || coordinateInputError(hotelCoordinatesText) === null
      ? coordinatesForSave(hotelCoordinatesText)
      : {}),
    // Countries: comma-separated ISO-2 input -> string[] (Block 5.6 country KB).
    countryCodes: (countryCodesText || "")
      .split(",")
      .map((s: string) => s.trim().toUpperCase())
      .filter((s: string) => /^[A-Z]{2}$/.test(s)),
    included: (data.included || []).map((item) => item.text),
    excluded: (data.excluded || []).map((item) => item.text),
    // Drop blank optional-activity rows ("Add activity" appends an empty one) so the
    // element-level name.min(1) can't 400 the entire trip save and lose unrelated edits.
    optionalActivities: (data.optionalActivities || [])
      .filter((a) => a?.name?.trim())
      .map((a) => ({
        ...a,
        name: a.name.trim(),
        priceNote: a.priceNote?.trim() || undefined,
        description: a.description?.trim() || undefined,
      })),
    hotelRooms: emptyToNull(
      (data.hotelRooms || [])
        .filter((room) => room?.name?.trim())
        .map((room) => ({
          ...room,
          id: room.id || generateId("room"),
          name: room.name.trim(),
          occupancy: room.occupancy?.trim() || undefined,
          beds: room.beds?.trim() || undefined,
          sizeSqm: room.sizeSqm ?? null,
          description: room.description?.trim() || undefined,
          surcharge: room.surcharge ?? 0,
          imageId: room.imageId ?? null,
        })),
    ),
    hotelMealPlans: emptyToNull(data.hotelMealPlans || []),
    hotelDepartures: emptyToNull(data.hotelDepartures || []),
    hotelFeatures: emptyToNull(data.hotelFeatures || []),
    // Kusovník: send only the template fields (drop the display-only joined `component`).
    components: (data.components || []).map((cmp, i) => ({
      id: cmp.id,
      componentId: cmp.componentId,
      quantity: cmp.quantity,
      seedTarget: cmp.seedTarget,
      sortOrder: i,
      notes: cmp.notes ?? null,
    })),
  };
}

export type { UnsavedTripPart };

/**
 * Neuložené části z odpovědi založení (`unsavedParts`, jen při neúplném uložení). Typ
 * odpovědi `ApiResponse<Trip>` klíč nezná, proto `unknown`. Jiný tvar, neznámá hodnota
 * nebo chybějící klíč = žádná neuložená část.
 */
export function unsavedTripParts(response: unknown): UnsavedTripPart[] {
  if (typeof response !== "object" || response === null) return [];
  const parts = (response as { unsavedParts?: unknown }).unsavedParts;
  if (!Array.isArray(parts)) return [];
  return UNSAVED_TRIP_PARTS.filter((part) => parts.includes(part));
}

/**
 * Keš `["tripUpgrades", id]` po úspěšném uložení přiřazení: data v pořadí uložených
 * `upgradeIds`, existující řádek se převezme, nový dostane prázdné `id` (doplní refetch).
 * Bez načtených přiřazení (`prev === undefined`) se keš nemění.
 */
export function assignmentsAfterSave(
  prev: ApiResponse<TripPremiumUpgrade[]> | undefined,
  tripId: string,
  upgradeIds: readonly string[],
): ApiResponse<TripPremiumUpgrade[]> | undefined {
  if (prev === undefined) return undefined;
  const byUpgrade = new Map(prev.data.map((a) => [a.upgradeId, a]));
  return {
    ...prev,
    data: upgradeIds.map((upgradeId, i) => {
      const existing = byUpgrade.get(upgradeId);
      return existing
        ? { ...existing, sortOrder: i }
        : { id: "", tripId, upgradeId, sortOrder: i, createdAt: new Date() };
    }),
  };
}
